import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

/**
 * i18n runtime of the SPA.
 *
 * Catalogues live in `locales/<locale>/<namespace>.json`, one namespace per
 * zone of the app, and are loaded on demand: every file is its own Vite chunk
 * (`import.meta.glob`), fetched the first time a component calls
 * `useTranslation("<namespace>")`.
 *
 * Keys are flat, UPPER_SNAKE_CASE and English (`keySeparator: false`). There
 * is no fallback language on purpose: a missing key renders as the key itself
 * (`EMPTY_STATE_TITLE`), which is impossible to miss in review or QA, whereas
 * a silent fallback to the other locale would hide the gap.
 *
 * The displayed language is resolved in this order:
 *   1. the user's saved preference (`users.locale`, applied by `LocaleSync`);
 *   2. the browser locale — French-speaking → `fr`, anything else → `en`;
 *   3. `fr`.
 */
export const SUPPORTED_LOCALES = ["fr", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "fr";

export const isLocale = (value: unknown): value is Locale =>
  typeof value === "string" &&
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

/** `fr-FR`, `fr-CA`, `fr` → `fr`; everything else → `en`. */
export const localeFromLanguageTag = (tag: string): Locale =>
  tag.toLowerCase().startsWith("fr") ? "fr" : "en";

const catalogues = import.meta.glob("../../locales/*/*.json");

export const i18n = i18next
  .use(LanguageDetector)
  .use(
    resourcesToBackend((language: string, namespace: string) => {
      const load = catalogues[`../../locales/${language}/${namespace}.json`];
      if (!load) {
        return Promise.reject(
          new Error(`No catalogue for ${language}/${namespace}`),
        );
      }
      return load();
    }),
  )
  .use(initReactI18next);

// `init` resolves the language asynchronously (no inline resources): the
// "fallback to fr" step must wait for the detector, otherwise it would win.
void i18n
  .init({
    supportedLngs: [...SUPPORTED_LOCALES],
    load: "languageOnly",
    fallbackLng: false,
    ns: ["common"],
    defaultNS: "common",
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ["navigator"],
      caches: [],
      convertDetectedLanguage: localeFromLanguageTag,
    },
    react: { useSuspense: true },
  })
  .then(() => {
    if (!isLocale(i18n.language)) return i18n.changeLanguage(DEFAULT_LOCALE);
  });

/** Current locale, always one of `SUPPORTED_LOCALES`. */
export const currentLocale = (): Locale =>
  isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
